/**
 * Agent-Chat useFiles Hook
 * Zustand store for file management with latest APIs
 */

import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import type { FileStore, UploadedFile, FileStatus, FileInfo, WorksheetData } from '../types/files'
import { apiClient } from '../services/api'
import { readRefusal } from '../services/api-client'

// Extended FileStore with isLoading property
interface ExtendedFileStore extends FileStore {
  isLoading: boolean
  /** The failed read itself, for screens that word it from its typed fields (ADR-20260925). */
  failure: unknown
  loadFiles: () => Promise<void>
}

export const useFileStore = create<ExtendedFileStore>()(
  subscribeWithSelector((set, get) => ({
    // State
    files: [],
    isUploading: false,
    isLoading: false,
    failure: null,
    uploadProgress: {},
    error: null,
    totalSize: 0,
    maxFileSize: 100 * 1024 * 1024, // 100MB
    allowedTypes: ['.xlsx', '.csv', '.json', '.parquet', '.xls'],

    // Actions
    loadFiles: async (): Promise<void> => {
      set({ isLoading: true, error: null, failure: null })
      try {
        const response = await apiClient.get<{
          files: Array<{
            file_id: string
            filename: string
            original_filename: string
            file_size: number
            content_type: string
            status: string
            created_at: string
            category: string
            metadata?: Record<string, any>
          }>
          total: number
        }>('/v1/files', { page: '1', size: '100' })

        // Convert backend file format to frontend format
        const files: UploadedFile[] = response.files.map(file => ({
          id: file.file_id,
          name: file.filename,
          size: file.file_size,
          type: file.content_type || 'application/octet-stream',
          status: (file.status === 'processed' || file.status === 'uploaded') ? 'completed' : 'error' as FileStatus,
          progress: 100,
          uploadedAt: new Date(file.created_at),
          worksheets: [],
          metadata: file.metadata
        }))

        const totalSize = files.reduce((sum, f) => sum + f.size, 0)

        set({
          files,
          totalSize,
          isLoading: false,
          error: null
        })

        console.log(`Loaded ${files.length} files from backend`)
      } catch (error) {
        console.error('Failed to load files:', error)
        set({
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load files',
          failure: error,
        })
      }
    },
    uploadFiles: async (files: File[]): Promise<UploadedFile[]> => {
      const { maxFileSize, allowedTypes } = get()
      set({ isUploading: true, error: null })

      try {
        // Validate files
        const invalidFiles = files.filter(file => {
          const extension = '.' + file.name.split('.').pop()?.toLowerCase()
          return file.size > maxFileSize || !allowedTypes.includes(extension)
        })

        if (invalidFiles.length > 0) {
          throw new Error(`Invalid files: ${invalidFiles.map(f => f.name).join(', ')}`)
        }

        // Create file records with temporary IDs
        const uploadedFiles: UploadedFile[] = files.map(file => ({
          id: crypto.randomUUID(),
          name: file.name,
          size: file.size,
          type: file.type || 'application/octet-stream',
          status: 'uploading' as FileStatus,
          progress: 0,
          uploadedAt: new Date(),
        }))

        // Add files to state
        set(state => ({
          files: [...state.files, ...uploadedFiles],
          totalSize: state.totalSize + files.reduce((sum, f) => sum + f.size, 0),
        }))

        // Upload each file using two-step process:
        // 1. Upload binary to storage: POST /v1/governance/storage/upload
        // 2. Register metadata: POST /v1/files
        const results: UploadedFile[] = []

        for (let i = 0; i < files.length; i++) {
          const file = files[i]!
          const uploadedFile = uploadedFiles[i]!

          try {
            // Update progress: starting upload
            set(state => ({
              uploadProgress: {
                ...state.uploadProgress,
                [uploadedFile.id]: 25,
              },
            }))

            // Step 1: Upload file binary to storage
            const formData = new FormData()
            formData.append('file', file)
            formData.append('container', 'uploads') // Default container
            formData.append('path', `files/${Date.now()}-${file.name}`)

            const storageResponse = await fetch('/v1/governance/storage/upload', {
              method: 'POST',
              body: formData,
              credentials: 'include', // Include session cookies
            })

            if (!storageResponse.ok) {
              throw new Error((await readRefusal(storageResponse)) ?? 'Storage upload failed')
            }

            const storageResult = await storageResponse.json()
            console.log('Storage upload response:', storageResult)

            // Update progress: storage upload complete
            set(state => ({
              uploadProgress: {
                ...state.uploadProgress,
                [uploadedFile.id]: 60,
              },
            }))

            // Step 2: Register file metadata with /v1/files
            const metadataResponse = await apiClient.post<{
              file_id: string
              filename: string
              status: string
            }>('/v1/files', {
              filename: file.name,
              original_filename: file.name,
              file_size: file.size,
              content_type: file.type || 'application/octet-stream',
              blob_path: storageResult.path || storageResult.blob_path || `files/${Date.now()}-${file.name}`,
              category: 'user_upload',
              status: 'uploaded',
            })

            console.log('File metadata response:', metadataResponse)

            // Update progress: complete
            const newFileId = metadataResponse.file_id || uploadedFile.id
            set(state => ({
              files: state.files.map(f =>
                f.id === uploadedFile.id
                  ? {
                      ...f,
                      id: newFileId,
                      status: 'completed' as FileStatus,
                      progress: 100,
                      processedAt: new Date(),
                      metadata: storageResult,
                    }
                  : f
              ),
              uploadProgress: {
                ...state.uploadProgress,
                [uploadedFile.id]: 100,
                [newFileId]: 100,
              },
            }))

            results.push({ ...uploadedFile, id: newFileId, status: 'completed' as FileStatus })
          } catch (error) {
            console.error(`Failed to upload file ${file.name}:`, error)

            // Mark this file as error
            set(state => ({
              files: state.files.map(f =>
                f.id === uploadedFile.id
                  ? {
                      ...f,
                      status: 'error' as FileStatus,
                      error: error instanceof Error ? error.message : 'Upload failed',
                      progress: 0,
                    }
                  : f
              ),
              uploadProgress: {
                ...state.uploadProgress,
                [uploadedFile.id]: 0,
              },
            }))

            results.push({
              id: uploadedFile.id,
              name: uploadedFile.name,
              size: uploadedFile.size,
              type: uploadedFile.type,
              status: 'error' as FileStatus,
              progress: 0,
              uploadedAt: uploadedFile.uploadedAt,
              error: error instanceof Error ? error.message : 'Upload failed',
            })
          }
        }

        set({ isUploading: false })
        return results
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed'
        set({ error: errorMessage, isUploading: false })
        throw error
      }
    },

    removeFile: async (fileId: string) => {
      try {
        // Call backend to delete file
        await apiClient.delete(`/v1/files/${fileId}`)

        // Remove from local state after successful backend deletion
        set(state => {
          const file = state.files.find(f => f.id === fileId)
          const newFiles = state.files.filter(f => f.id !== fileId)
          const newTotalSize = file ? state.totalSize - file.size : state.totalSize
          const newProgress = { ...state.uploadProgress }
          delete newProgress[fileId]

          return {
            files: newFiles,
            totalSize: newTotalSize,
            uploadProgress: newProgress,
          }
        })

        console.log(`File ${fileId} deleted successfully`)
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to delete file'
        set({ error: errorMessage })
        throw error
      }
    },


    renameFile: (fileId: string, newName: string) => {
      set(state => ({
        files: state.files.map(f =>
          f.id === fileId ? { ...f, name: newName } : f
        )
      }))
    },

    retryUpload: async (fileId: string) => {
      // Implementation would retry the specific file upload
      set(state => ({
        uploadProgress: {
          ...state.uploadProgress,
          [fileId]: 0,
        },
      }))

      get().updateFileStatus(fileId, 'uploading', 0)

      // Actual retry logic would go here
      setTimeout(() => {
        get().updateFileStatus(fileId, 'completed', 100)
      }, 2000)
    },

    clearFiles: () => {
      set({
        files: [],
        uploadProgress: {},
        totalSize: 0,
        error: null,
      })
    },

    updateFileStatus: (fileId: string, status: FileStatus, progress?: number) => {
      set(state => ({
        files: state.files.map(file =>
          file.id === fileId
            ? {
                ...file,
                status,
                progress: progress !== undefined ? progress : file.progress,
                processedAt: status === 'completed' ? new Date() : file.processedAt,
                error: status === 'error' ? 'Upload failed' : undefined,
              }
            : file
        ),
        uploadProgress: progress !== undefined
          ? { ...state.uploadProgress, [fileId]: progress }
          : state.uploadProgress,
      }))
    },

    setError: (error: string | null) => {
      set({ error })
    },

    getFileInfo: async (fileId: string): Promise<FileInfo> => {
      try {
        const response = await apiClient.get<{ status: string; data: FileInfo }>(`/v1/files/${fileId}/info`)
        return response.data
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to get file info'
        set({ error: errorMessage })
        throw error
      }
    },

    getWorksheetData: async (fileId: string, worksheetId: string, limit = 100, offset = 0): Promise<WorksheetData> => {
      try {
        const response = await apiClient.get<{ status: string; data: WorksheetData }>(
          `/v1/files/${fileId}/worksheets/${worksheetId}/data`,
          { limit: limit.toString(), offset: offset.toString() }
        )
        return response.data
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to get worksheet data'
        set({ error: errorMessage })
        throw error
      }
    },
  }))
)
